// Configs for the SEO landing pages at /model-contract-inchiriere-*.
// Page 1 is production-ready. Pages 2-4 are stubbed (status: "draft")
// until content is finalized; they are not yet registered as routes in App.js.

const MODELS = {
  "model-contract-inchiriere-pdf": {
    status: "live",
    slug: "model-contract-inchiriere-pdf",
    crumbLabel: "Model PDF",
    seoTitle: "Model Contract de Închiriere PDF — Descarcă Gratis 2026 | ContractGen",
    seoDescription:
      "Descarcă gratuit un model de contract de închiriere PDF, conform Codului Civil 2026. Sau personalizează-l cu datele tale în 2 minute și generează un contract valid.",
    ogType: "article",

    h1: "Model Contract de Închiriere PDF — Descarcă Gratis 2026",
    subtitle:
      "Template conform Codului Civil, actualizat pentru 2026. Descarcă PDF-ul sau personalizează-l cu datele tale în 2 minute.",

    pdfFilename: "model-contract-inchiriere-pdf.pdf",
    pdfLabel: "Descarcă PDF model (gratis)",
    generatorCtaLabel: "Personalizează cu datele tale →",
    generatorPath: "/contract-inchiriere",

    previewCaption: "Prima pagină a modelului de contract — A4, 10 articole principale",

    whatsIncluded: {
      title: "Ce conține modelul",
      intro:
        "Modelul acoperă toate clauzele standard ale unui contract de închiriere de locuință din România:",
      items: [
        "Datele părților — proprietar și chiriaș (persoană fizică sau juridică)",
        "Datele imobilului — tip, adresă, suprafață, componență",
        "Destinația — locuință sau spațiu comercial",
        "Durata contractului și data de începere",
        "Chiria lunară, valuta, scadența și modalitatea de plată",
        "Garanția — cuantum și condiții de restituire",
        "Utilități — cine plătește și în ce termene",
        "Obligațiile proprietarului și ale chiriașului",
        "Condițiile de reziliere, preavizul și penalități pentru neplată",
        "Clauze speciale — subînchiriere, animale, fumat, număr de persoane",
      ],
    },

    educationalSections: [
      {
        title: "Ce trebuie să conțină un contract de închiriere valid în România",
        paragraphs: [
          "Conform Codului Civil (Legea 287/2009, art. 1777–1823), locațiunea este contractul prin care o parte (locatorul, respectiv proprietarul) se obligă să asigure celeilalte părți (locatarul, respectiv chiriașul) folosința unui bun pentru o anumită perioadă, în schimbul unui preț numit chirie.",
          "Forma scrisă nu este cerută pentru validitate — contractul este consensual și se naște din acordul părților — dar este obligatorie ca mijloc de probă în fața instanței. Fără un înscris, partea care invocă contractul va avea mari dificultăți să-și dovedească drepturile. Un model PDF completat și semnat rezolvă această problemă.",
          "Elementele esențiale pe care le acoperă orice contract de închiriere sunt: părțile (cu date de identificare complete), bunul închiriat (cu adresă și descriere), chiria (cuantum, valută, scadență), durata și modalitatea de folosință. Lipsa oricăruia dintre aceste elemente poate atrage nulitatea sau dificultăți practice la executare.",
          "Contractul se încheie în minimum 2 exemplare originale (unul pentru fiecare parte). Durata maximă este de 49 de ani — o durată mai lungă se reduce de drept la 49 (art. 1783 CC).",
        ],
      },
      {
        title: "Cadrul legal aplicabil în 2026",
        paragraphs: [
          "Sursele principale pentru un contract de închiriere sunt: Codul Civil (art. 1777–1823, regulile generale despre locațiune), Legea locuinței 114/1996 (cerințe minime pentru spațiile destinate locuirii) și OUG 40/1999 (protecția chiriașilor, cu reguli specifice pentru anumite contracte reziliate).",
          "Pe latura fiscală, veniturile din închiriere obținute de persoane fizice sunt impozitate conform Codului fiscal (art. 84 și următoarele). Cota standard este de 10%, cu deducere forfetară de 20% din venitul brut. Din 2024, impozitul se reține la sursă dacă chiriașul este persoană juridică.",
          "Pentru spațiile comerciale, se aplică în principal Codul Civil — nu Legea 114/1996. În plus, dacă proprietarul este plătitor de TVA, chiria poate fi supusă TVA (cu sau fără opțiunea de taxare, după caz).",
        ],
      },
      {
        title: "Model generic vs contract personalizat",
        paragraphs: [
          "Un model PDF descărcat este un punct de plecare util: arată structura, clauzele standard și formularea juridică acceptată. Pentru a produce efecte între părți, el trebuie însă completat cu date reale — CNP-uri valide, sume exacte, adrese complete, termene negociate.",
          [
            "Un contract personalizat (generat cu un formular precum cel de pe ",
            { link: "/contract-inchiriere", text: "generator-contracte.com/contract-inchiriere" },
            ") acoperă automat validări pe care un PDF gol nu le poate face: format corect pentru CNP și CUI, calcul automat al garanției, adaptare a clauzelor după tipul de imobil (apartament vs spațiu comercial) și după regimul părților (persoană fizică vs juridică).",
          ],
          "Recomandarea practică: descarcă modelul pentru a vedea cum arată, apoi generează-ți varianta personalizată pentru semnare. Modelul rămâne util ca referință atunci când negociezi clauzele.",
        ],
      },
      {
        title: "Înregistrarea la ANAF — când și cum",
        paragraphs: [
          "Înregistrarea la ANAF NU este o condiție de validitate a contractului. Un contract semnat și nedeclarat este, între părți, la fel de obligatoriu ca unul înregistrat.",
          "Înregistrarea schimbă însă două lucruri importante. Primul: contractul înregistrat devine titlu executoriu pentru plata chiriei și pentru evacuare (art. 1798 CC), ceea ce scurtează dramatic procedura judiciară în caz de neplată. Al doilea: este premisa declarării corecte a venitului și calculării impozitului.",
          [
            "Formularul pentru înregistrarea contractului este ",
            { text: "Formularul 168", bold: true },
            " — „Cerere pentru înregistrarea contractelor de locațiune”, depus la ANAF (prin SPV sau ghișeu) în termen de 30 de zile de la încheiere. Separat, proprietarul persoană fizică depune ",
            { text: "Formularul 212", bold: true },
            " (Declarația unică) pentru impozitul pe venit. Mai multe detalii practice în ",
            { link: "/blog/inregistrare-contract-inchiriere-anaf", text: "ghidul nostru despre înregistrarea contractului la ANAF" },
            ".",
          ],
        ],
      },
    ],

    whyPersonalizeSection: {
      title: "De ce să personalizezi în loc să completezi manual",
      items: [
        {
          title: "Date proprii completate automat",
          body: "Introduci o singură dată CNP-ul, CUI-ul, adresele — apar corect în toate clauzele.",
        },
        {
          title: "Calcule și validări integrate",
          body: "Garanție calculată din chirie, verificare format CNP/CUI/IBAN, calcul data de sfârșit din durată.",
        },
        {
          title: "Clauze adaptate la situația ta",
          body: "Spațiu comercial vs locuință, persoană fizică vs juridică, TVA da/nu — clauzele se schimbă automat.",
        },
        {
          title: "PDF gata de semnat",
          body: "Descarci direct un PDF A4 cu toate datele completate, formatat pentru imprimare sau semnare electronică.",
        },
      ],
      ctaLabel: "Generează contractul meu personalizat →",
    },

    faq: [
      {
        q: "Este valabil un contract de închiriere fără notar?",
        a:
          "Da. Contractul de închiriere este consensual — se încheie prin simplul acord al părților (art. 1166 Cod Civil) și nu necesită formă autentică notarială pentru a fi valabil. Forma autentică este cerută numai în cazuri speciale (de exemplu, pentru înscrierea în cartea funciară pe termene foarte lungi). Un contract scris și semnat de ambele părți produce toate efectele juridice.",
      },
      {
        q: "Trebuie înregistrat contractul la ANAF?",
        a:
          "Înregistrarea nu este obligatorie pentru validitatea contractului. Este însă puternic recomandată: un contract înregistrat la ANAF (prin Formularul 168) devine titlu executoriu conform art. 1798 Cod Civil, ceea ce înseamnă că evacuarea pentru neplată și recuperarea chiriei se pot face fără proces civil prealabil. În plus, înregistrarea este premisa declarării corecte a venitului din chirii.",
      },
      {
        q: "Ce garanție maximă pot cere chiriașului?",
        a:
          "Legea nu stabilește un plafon legal pentru garanție. În practica pieței, garanția standard este echivalentul a 1 până la 2 chirii lunare. Trebuie precizată explicit în contract (cuantum, termen de restituire, condiții de reținere). La încetarea contractului, garanția se restituie minus chiriile restante, utilitățile neachitate și eventualele daune produse de chiriaș.",
      },
      {
        q: "Pe ce durată se încheie un contract de închiriere?",
        a:
          "Durata este liberă între părți. În practică, contractele de locuință se încheie pe 12 luni, cu posibilitate de prelungire prin act adițional. Durata maximă admisă de lege este de 49 de ani (art. 1783 Cod Civil) — o durată mai lungă se reduce de drept la 49 de ani. Pentru spații comerciale, durate de 3–5 ani sunt comune.",
      },
      {
        q: "Ce se întâmplă dacă chiriașul nu plătește chiria?",
        a:
          "Neplata atrage rezilierea contractului. Dacă ați inclus un pact comisoriu expres (clauză de reziliere de plin drept), rezilierea operează automat la împlinirea termenului convenit, cu notificare prealabilă. Dacă nu, rezilierea se obține prin hotărâre judecătorească. Când contractul este înregistrat la ANAF, el este titlu executoriu — evacuarea și recuperarea sumelor se execută direct prin executor judecătoresc.",
      },
      {
        q: "Pot adăuga clauze proprii la modelul de contract?",
        a:
          "Da. Principiul libertății contractuale (art. 1169 Cod Civil) permite părților să negocieze orice clauză care nu contravine legii, ordinii publice sau bunelor moravuri. Clauze uzuale adăugate: interzicerea subînchirierii, regim pentru animale de companie, obligația chiriașului de a face reparații curente, penalități de întârziere, condiții privind modificările aduse imobilului.",
      },
    ],

    relatedLinks: {
      title: "Continuă cu",
      items: [
        { to: "/contract-inchiriere", label: "Generează un contract personalizat (formular rapid)" },
        { to: "/blog/inregistrare-contract-inchiriere-anaf", label: "Ghid: cum înregistrezi contractul la ANAF" },
        { to: "/blog/reziliere-contract-inchiriere", label: "Ghid: rezilierea contractului de închiriere" },
        { to: "/blog/fara-contract-inchiriere-riscuri", label: "Ce riscuri ai fără un contract scris" },
      ],
    },
  },

  // --- TODO: populate after page 1 is approved ---
  "model-contract-inchiriere-apartament": {
    status: "draft",
    slug: "model-contract-inchiriere-apartament",
    pdfFilename: "model-contract-inchiriere-apartament.pdf",
  },
  "model-contract-inchiriere-anaf": {
    status: "draft",
    slug: "model-contract-inchiriere-anaf",
    pdfFilename: "model-contract-inchiriere-anaf.pdf",
  },
  "model-contract-inchiriere-spatiu-comercial": {
    status: "draft",
    slug: "model-contract-inchiriere-spatiu-comercial",
    pdfFilename: "model-contract-inchiriere-spatiu-comercial.pdf",
  },
};

export default MODELS;
